import { useState } from 'react';
import { useAuth } from '../../../auth/firebaseAuth';
import {
  getDocumentById,
  insertDocument,
  updateDocument
} from '../../../utility/firebaseHelpers';
import { Timestamp } from 'firebase/firestore';
import { remoteConfig } from '../../../utility/firebaseRemoteConfigHelper';
import { useAnalyticsWithConsent } from '../../../hooks/useAnalyticsWithConsent';
import { AnalyticsEvents } from '../../../analytics/firebaseAnalytics';
import toast from 'react-hot-toast';
import { Listing } from '../../../components/property/property-card/PropertyCardTypes';

interface AutoApplyUser {
  userId: string;
  isEnabled: boolean;
  shareCount?: number;
  sharedProperties?: Array<{
    propertyId: string;
    sharedAt: Timestamp;
    sharedWith: string;
  }>;
  stats?: {
    totalApplications: number;
    successfulApplications: number;
  };
}

interface UseShareResult {
  isShareModalOpen: boolean;
  currentShareUrl: string;
  currentShareTitle: string;
  setIsShareModalOpen: (open: boolean) => void;
  handleShare: (
    listing: Listing,
    currentPage: number,
    listings: Listing[]
  ) => Promise<void>;
}

const getPriceRange = (price: number): string => {
  if (price < 1000) return 'low';
  if (price < 2000) return 'medium';
  return 'high';
};

export const useShare = (): UseShareResult => {
  const { currentUser } = useAuth();
  const analytics = useAnalyticsWithConsent();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [currentShareTitle, setCurrentShareTitle] = useState('');

  const handleShare = async (
    listing: Listing,
    currentPage: number,
    listings: Listing[]
  ) => {
    analytics.logEvent(AnalyticsEvents.SHARE_INTERACTION, {
      property_id: listing.id,
      property_type: listing.type,
      price_range: getPriceRange(listing.price),
      source: 'search_results',
      page_number: currentPage,
      position_in_results: listings.findIndex((l) => l.id === listing.id) + 1
    });

    const shareUrl = `${window.location.origin}/property/${listing.id}`;
    const shareTitle = `Check out this property on RentSmart.ie: ${listing.title}`;
    setCurrentShareUrl(shareUrl);
    setCurrentShareTitle(shareTitle);
    setIsShareModalOpen(true);

    // Track share for Auto Apply feature if user is logged in and remote config: isAutoApplyEnabled is enabled
    if (currentUser && remoteConfig.isAutoApplyEnabled()) {
      try {
        // Get existing auto apply user document
        const autoApplyUser = await getDocumentById<AutoApplyUser>(
          'autoApplyUsers',
          currentUser.uid
        );

        if (autoApplyUser) {
          // Check if property already shared
          const alreadyShared = autoApplyUser.sharedProperties?.some(
            (share) => share.propertyId === listing.id.toString()
          );

          if (!alreadyShared) {
            const updatedShareCount = (autoApplyUser.shareCount || 0) + 1;
            if (updatedShareCount === 1) {
              toast.success(
                'Share one more property to activate auto-apply feature!'
              );
            }
            const updatedProperties = [
              ...(autoApplyUser.sharedProperties || []),
              {
                propertyId: listing.id.toString(),
                sharedAt: Timestamp.now(),
                sharedWith: 'social_share'
              }
            ];

            // Update document
            await updateDocument('autoApplyUsers', currentUser.uid, {
              shareCount: updatedShareCount,
              sharedProperties: updatedProperties,
              lastUpdated: Timestamp.now()
            });

            // Check if user has reached share threshold
            if (updatedShareCount >= 2 && !autoApplyUser.isEnabled) {
              await updateDocument('autoApplyUsers', currentUser.uid, {
                isEnabled: true,
                activatedAt: Timestamp.now(),
                expiresAt: Timestamp.fromDate(
                  new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                )
              });

              toast.success(
                'Auto Apply feature activated! Go to auto-apply dashboard to set your preferences.'
              );
            }
          }
        } else {
          // Create new auto_apply_users document
          await insertDocument(
            'autoApplyUsers',
            {
              userId: currentUser.uid,
              isEnabled: false,
              shareCount: 1,
              sharedProperties: [
                {
                  propertyId: listing.id.toString(),
                  sharedAt: Timestamp.now(),
                  sharedWith: 'social_share'
                }
              ],
              createdAt: Timestamp.now(),
              lastUpdated: Timestamp.now(),
              stats: {
                totalApplications: 0,
                successfulApplications: 0
              }
            },
            currentUser.uid
          );
        }
      } catch (error) {
        console.error('Error tracking share for Auto Apply:', error);
      }
    }
  };

  return {
    isShareModalOpen,
    currentShareUrl,
    currentShareTitle,
    setIsShareModalOpen,
    handleShare
  };
};
